import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import { PulseLoader, PuffLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faShop, faXmark } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

import Overview from './Overview/Overview';
import Products from './Products/Products';
import Services from './Services/Services';
import Settings from './Settings/Settings';
import Orders from './Orders/Orders';
import CreateAccount from '../CreateAccount/CreateAccount';

import errorMsg from '../../../Functions/errorMsg';

import { useLoading } from '../../../Context/useLoading';
import Image from 'next/image';

export default function Manager() {
  const { data: session, status } = useSession();
  const { processExists, addProcess, removeProcess } = useLoading();
  
  const [initialCheckHappened, setInitialCheckHappened] = useState(false)

  const [accountCreated, setAccountCreated] = useState(null);

  const [hasError, setHasError] = useState(false);
  const [vendorData, setVendorData] = useState(null);

  const [nav, setNav] = useState('Overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pfpLoading, setPfpLoading] = useState(true);

  const defaultIcon = <FontAwesomeIcon icon={faShop} id={styles.icon} />;

  const handleIncomingComponentData = (data) => {
    setVendorData(prevData => ({
      ...prevData,
      ...data,
    }));
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 850) {
        setMobileMenuOpen(false); // Close the mobile menu if switching to desktop
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize); // Cleanup listener
    };
  }, []);

  // Fetch account creation state
  useEffect(() => {
    if (!session || status !== 'authenticated' || initialCheckHappened) return;

    async function verifyAccountCreated() {
      addProcess('accountCreated');
      try {
        const res = await axios.get('/api/vendor/helpers/isAccountCreated');

        if(res && res.status === 200) {
          setAccountCreated(res.data);
          setInitialCheckHappened(true);
        }

      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setAccountCreated(false);
      } finally {
        removeProcess('accountCreated');
      }
    }

    verifyAccountCreated();
  }, [session, status, addProcess, removeProcess]);

  // Fetch vendor data if account exists
  useEffect(() => {
    if (!session || accountCreated !== true || vendorData || hasError) return;

    async function getVendorData() {
      addProcess('accountData');
      try {
        const res = await axios.get('/api/vendor/helpers/getManagerData');

        if(res && res.status === 200) {
          setVendorData(res.data);
        }

      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        removeProcess('accountData');
      }
    }

    getVendorData();
  }, [accountCreated, session, addProcess, removeProcess, hasError]);

  const toggleMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  if (accountCreated === false) {
    return <CreateAccount setAccountCreated={setAccountCreated} />;
  }

  if (!session || status === 'loading' || processExists('accountCreated') || processExists('accountData') || !vendorData || hasError) {
    return (
      <section id={styles.loading}>
        <PulseLoader size={30} color="white" />
      </section>
    );
  }

  const mobileMenuVariants = {
    hidden: { x: '100%', opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 40 } },
    exit: { x: '100%', opacity: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
  };

  return (
    <section id={styles.wrapper}>
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.nav
            id={styles.mobileMenu}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={mobileMenuVariants}
          >
            <FontAwesomeIcon onClick={toggleMenu} icon={faXmark} id={styles.close} />
            <section>
              <NavItem name="Overview" nav={nav} setNav={setNav} closeMenu={() => setMobileMenuOpen(false)} />
              <NavItem name="My Products" nav={nav} setNav={setNav} closeMenu={() => setMobileMenuOpen(false)} />
              <NavItem name="My Services" nav={nav} setNav={setNav} closeMenu={() => setMobileMenuOpen(false)} />
              <NavItem name="Orders" nav={nav} setNav={setNav} closeMenu={() => setMobileMenuOpen(false)} />
              <NavItem name="Settings" nav={nav} setNav={setNav} closeMenu={() => setMobileMenuOpen(false)} />
            </section>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav id={styles.shopNav}>
        <section id={styles.shopDetails}>
          {vendorData.pfp ? (
            <div onClick={() => setNav('Settings')}>
              {pfpLoading && <PuffLoader size={20} color="white" />}
              <Image
                sizes="auto auto"
                fill
                objectFit="cover"
                src={vendorData.pfp}
                alt="Vendor Profile"
                onLoadingComplete={() => setPfpLoading(false)}
              />
            </div>
          ) : (
            <div onClick={() => setNav('Settings')}>{defaultIcon}</div>
          )}
          <h1 onClick={() => setNav('Settings')}>{vendorData.displayName || "Default Name"}</h1>
        </section>

        <section id={styles.menu}>
          <div className={styles.menuDesktop}>
            <NavItem name="Overview" nav={nav} setNav={setNav} />
            <NavItem name="My Products" nav={nav} setNav={setNav} />
            <NavItem name="My Services" nav={nav} setNav={setNav} />
            <NavItem name="Orders" nav={nav} setNav={setNav} />
            <NavItem name="Settings" nav={nav} setNav={setNav} />
          </div>
          <div onClick={toggleMenu} className={styles.menuMobile}>
            <FontAwesomeIcon icon={faBars} id={styles.openIcon} />
          </div>
        </section>
      </nav>

      {/* Content */}
      {nav === 'Overview' && <Overview />}
      {nav === 'My Products' && <Products />}
      {nav === 'My Services' && <Services />}
      {nav === 'Orders' && <Orders />}
      {nav === 'Settings' && <Settings sendHeaderData={handleIncomingComponentData} />}
    </section>
  );
}

const NavItem = ({ name, nav, setNav, closeMenu = () => {} }) => (
  <button
    onClick={() => {
      if(nav === name) return;
      setNav(name);
      closeMenu();
    }}
    className={nav === name ? styles.navActive : ''}
  >
    {name}
  </button>
);
